import { elements, type Element } from "@/data/elements";

/**
 * Periodic Sprint - pure question-generation + scoring logic.
 *
 * Each question shows an element's name and asks the player to find it on
 * a compact periodic-table grid (no labels visible by default - see the
 * page for "show symbols" assist). Scoring matches Glassware ID / Speed
 * Quiz so muscle memory carries between games.
 */

export const SPRINT_TURN_SECONDS = 12;
export const SPRINT_FAST_THRESHOLD_S = 4;
export const SPRINT_POINTS_FAST = 15;
export const SPRINT_POINTS_SLOW = 8;

export type SprintOutcome = "fast" | "slow" | "wrong" | "timeout";

export interface SprintQuestion {
  /** Element atomic number - stable id for de-dupe. */
  id: string;
  target: Element;
}

function pickRandom<T>(rng: () => number, arr: T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/**
 * Build a single question. Tries to honour `excludeIds`; falls back to
 * the unfiltered pool if every candidate would be excluded.
 */
export function generateQuestion(
  rng: () => number,
  excludeIds: ReadonlySet<string>,
): SprintQuestion {
  const available = elements.filter((e) => !excludeIds.has(String(e.number)));
  const pool = available.length > 0 ? available : elements;
  const target = pickRandom(rng, pool);
  return { id: String(target.number), target };
}

/** Resolve an answer attempt (atomic number chosen, or null on timeout). */
export function scoreAnswer(
  chosenNumber: number | null,
  question: SprintQuestion,
  elapsedMs: number,
): { points: number; outcome: SprintOutcome } {
  if (chosenNumber === null) return { points: 0, outcome: "timeout" };
  if (chosenNumber !== question.target.number) {
    return { points: 0, outcome: "wrong" };
  }
  if (elapsedMs <= SPRINT_FAST_THRESHOLD_S * 1000) {
    return { points: SPRINT_POINTS_FAST, outcome: "fast" };
  }
  return { points: SPRINT_POINTS_SLOW, outcome: "slow" };
}